<script lang="ts">
    import { fly, blur, scale, fade } from 'svelte/transition';
    import { get, writable } from 'svelte/store';
    import { Navigation } from '../../../utilities/Navigation';
    import { afterUpdate, onMount } from 'svelte';
    import { _ } from 'svelte-i18n';
    import * as Sentry from '@sentry/browser';

    import {
        TacticalPlanApi,
        type TacticalPlanResponseType,
    } from '../../TacticalPlan/TacticalPlanApi';
    import MessageNotFound from '../../../shared/Messages/MessageNotFound.svelte';
    import ScenarioOneSettings from './ScenarioOneSettings.svelte';
    import TemplateInner from '../../../shared/TemplateInner.svelte';
    import type { ScenarioResponseType } from '../ScenarioTypes';
    import MessageDanger from '../../../shared/Messages/MessageDanger.svelte';
    import FilterPlanning from '../../Filter/FilterPlanning.svelte';
    import type { VehicleResponseType } from '../../Vehicles/types';
    import AssignmentSummary from './AssignmentSummary.svelte';
    import { currentMainFilter } from '../../../../stores/filter';
    import { onMapReady } from '../../../utilities/onMapReady';
    import Spinner from '../../../shared/Spinner/Spinner.svelte';
    import { PlanningOnMap } from '../PlanningOnMap';
    import { debug, planning, readonlyMode, tacticalPlanStore } from '../../../../stores/store';
    import {
        AssignmentApi,
        type AssignmentResponseType,
        type EstimationRouteType,
    } from '../AssignmentApi';
    import { api, clientSettings } from '../../../../stores/api';
    import { ScenarioApi, type ScenarioToCompareResponseType } from '../ScenarioApi';
    import { scenarioManager } from '../../../../stores/store';
    import MessageInline from '../../../shared/Messages/MessageInline.svelte';
    import ErrorInline from '../../../shared/ErrorInline.svelte';
    import ButtonMain from '../../../shared/ButtonMain.svelte';
    import Separator from '../../../shared/Separator.svelte';
    import { AssignmentJobApi, type AssignmentJobResponseType, type AssignmentJobSuggestType } from '../AssignmentJobApi';
    import { Assignment } from '../Assignment.js';
    import { captureMessage } from '../../../utilities/Sentry';
    import { features } from '../../../shared/Features/features';
    import ScenarioOneQuickCategories from '../QuickCategories/ScenarioOneQuickCategories.svelte';
    import { QuickCategories } from '../QuickCategories/QuickCategories';
    import { quickCategoriesConfig } from '../QuickCategories/QuickCategoriesConfig';
    import { resetAssignments, setStartPlanning } from '../Planning';
    import WarningInline from '../../../shared/WarningInline.svelte';
    import { PlanningValidator } from '../PlanningValidator/PlanningValidator';
    import { PlanningContext } from '../PlanningValidator/PlanningContext';
    import { ValidatorReport } from '../../TacticalPlan/ValidatorReport';
    import { Const } from '../../../../stores/Const';
    import IconPending from '../../../shared/Icons/IconPending.svelte';
    import { vehiclesManager } from '../../Vehicles/VehiclesApp/vehiclesManager';
    import {
        type FunctionOnChangeIdsType,
        StageTargetProcessingChecker,
    } from './StageTargetProcessingChecker/StageTargetProcessingChecker';
    import { AssignmentJob } from '../AssignmentJob';
    import { MenuContextFactory } from '../../MenuDropdown/MenuContext/MenuContextFactory';
    import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
    import { Umami } from '../../../utilities/Umami';

    import MonitActionForAssignmentJobs from '../PageDeliveries2/MonitActionForAssignmentJobs.svelte';

    export let scenario: ScenarioResponseType;
    export let vehicles: VehicleResponseType[];
    export let onlyLocked: boolean = false;
    export let allScenarios: ScenarioResponseType[] = [];

    let loading: boolean = true;
    let error: string = '';
    let planningOnMap: PlanningOnMap;
    let leaveMapObjects: boolean = false;
    let tacticalPlan: TacticalPlanResponseType | null = null;
    let waitingMessage: string = '';
    let componentMounted: boolean = false;
    // let visibleAssignmentIds: number[] = [];
    let visibleAssignmentIds: number[] = [];
    let selectableAssignmentIds: number[] = [];
    let lockedAssignmentIds: number[] = [];

    let previousAssignmentQuery: string = '';

    // let filterHideMethod: 'hide' | 'opacity' = 'opacity';
    // let assignments: Assignment[] = [];
    let adHocAssignment: Assignment | null = null;

    let planningValidator = new PlanningValidator();
    let planningContext: PlanningContext | null = null;
    let planningReport = new ValidatorReport();
    let getRealisationStatusShouldWork: boolean = false;
    let getRealisationStatusPending: boolean = false;

    let zoomMap: boolean = true;
    let anyAssignmentsReadyToShow: boolean = false;
    let tacticalPlanLoaded: boolean = false;
    let scenarioToCompareTimeoutHandler: any = null;

    // const vehiclesOnMap = new VehiclesOnMap(get(api)).setUpdateIntervalSeconds(60);

    function setAssignmentColor(assid: number, color: string) {
        AssignmentApi.setColor(get(api), assid, color).then(() => {
            Umami.event('assignment_color_changed', { assignment_id: assid });
            if (planningOnMap) {
                planningOnMap.reload();
            }
            $scenarioManager.setColor(assid, color);
            let vehicleId = $scenarioManager?.getAssignmentById(assid)?.getVehicleId() ?? null;
            if (vehicleId) {
                (async () => {
                    await $vehiclesManager.setColor(vehicleId, color);
                    $vehiclesManager = $vehiclesManager;
                })();
            }
            // reloadVehiclesOnMap();
        });
    }

    let monitMapBar: any;

    function createMonitMapBar(items) {
        let mapBarElement = document.createElement('div');
        mapBarElement.style.cssText = 'width: 100%; height: 90px; background-color: rgba(255, 255, 255, 0.9);';
        let shadow = mapBarElement.attachShadow({mode: 'open'});
        Targeo.Base.Map.modules.Controls.addControl(
            'monit-map-bar',
            mapBarElement,
            {
                align: 'bottom-left',
                edge: true,
                visibilityPriority: 1000,
                attractPriority: 1000,
                onSpaceShortage: function() { return {}; }
            }
        );
        monitMapBar = new MonitActionForAssignmentJobs({
            target: shadow,
            props: {
                selectedItems: items,
                disabled: false,
                onAction: () => {
                    planningOnMap.jobSetSelection([]);
                    destroyMonitMapBar();
                    zoomMap = false;
                    reloadData();
                },
                onClose: () => {
                    planningOnMap.jobSetSelection([]);
                    destroyMonitMapBar();
                }
            }
        });
    }

    function destroyMonitMapBar() {
        Targeo.Base.Map.modules.Controls.removeControl('monit-map-bar');
        monitMapBar = null;
    }

    async function getScenarioToCompare(scenarioId: number): Promise<ScenarioToCompareResponseType> {
        let scenario = await ScenarioApi.getOne(get(api), scenarioId).catch(err => {
            captureMessage('getScenarioToCompare, getOne error: ' + err);
            throw err;
        });
        let assignments = await AssignmentApi.getWithCountersByScenarioId(get(api), scenarioId, $planning.selectedDay).catch(err => {
            captureMessage('getScenarioToCompare, getWithCountersByScenarioId error: ' + err);
            throw err;
        });
        let assignmentIds = assignments.map(assignment => assignment.id);
        let response = await Promise.all([
            AssignmentJobApi.getSuggestedAssignmentIds(
                get(api),
                assignmentIds,
                $planning.selectedDay
            ),
            ScenarioApi.getAssignmentJobsToCompare(get(api), scenarioId, {types: ['_unassigned']})
        ]);
        return {
            scenario: scenario,
            assignments: assignments,
            suggested_assignment_ids: response[0],
            unassigned_job_ids: response[1]
        };
    }

    function reloadData() {
        console.log('assignment=1519, ScenarioOne, reloadData');
        loading = true;
        // $scenarioManager.clear();
        $planning.lastUsedAssignment = null;
        $planning.assignments = 0;
        tacticalPlan = null;
        error = '';

        Promise.all([
            ScenarioApi.getOne(get(api), scenario.id),
            AssignmentApi.getWithCountersByScenarioId(get(api), scenario.id, $planning.selectedDay),
            ScenarioApi.getAdHocAssignment(get(api), scenario.id),
            onMapReady(),
            $planning.scenarioToCompare.enabled && $planning.scenarioToCompare.scenarioId!==null ? getScenarioToCompare($planning.scenarioToCompare.scenarioId) : Promise.resolve(null),
            // getRealisationStatus(),
        ])
            .then(response => {
                let assignmentIds: number[] = [];
                scenario = response[0];
                assignmentIds = response[1].map(assignment => assignment.id);
                if (
                    response[2] &&
                    response[2].details &&
                    Object.values(response[2].details).reduce((a, b) => a + b, 0) > 0
                ) {
                    adHocAssignment = new Assignment(response[2]);
                    let quickCategories = new QuickCategories(quickCategoriesConfig);
                    adHocAssignment.setQuickCategories(quickCategories);
                    adHocAssignment.updateQuickCategories();
                }

                AssignmentJobApi.getSuggestedAssignmentIds(
                    get(api),
                    assignmentIds,
                    $planning.selectedDay
                )
                    .then(async responseSuggest => {
                        $scenarioManager.clear();

                        response[1].forEach(rass => {
                            let assignment = new Assignment(rass);

                            responseSuggest.forEach((assSuggest: AssignmentJobSuggestType) => {
                                if (
                                    assSuggest.suggested_assignment_id >= 0 &&
                                    assignment.getId() === assSuggest.suggested_assignment_id
                                ) {
                                    assignment.addSuggestedJob(assSuggest.suggested_assignment_id);
                                }
                            });

                            let quickCategories = new QuickCategories(quickCategoriesConfig);
                            assignment.setQuickCategories(quickCategories);

                            $scenarioManager.updateSection(assignment);
                            if (assignment.get().stage_target_processed === false || assignment.isRouteInCalculation()) {
                                $scenarioManager.setAssignmentBusyStatus(assignment.getId(), true);
                            }
                        });

                        $scenarioManager.setVehicles(vehicles);

                        if (scenario) {
                            $scenarioManager.setScenario(scenario);
                            $scenarioManager.setAllScenarios(allScenarios);
                        }

                        // compareTo scenario
                        if (response[4]) {
                            let scenarioToCompare: ScenarioToCompareResponseType = response[4];
                            $scenarioManager.setScenarioToCompare(scenarioToCompare);
                            clearInterval(scenarioToCompareTimeoutHandler);
                            if ($planning.scenarioToCompare.calculatingInProgress) {
                                scenarioToCompareTimeoutHandler = setInterval(() => {
                                    ScenarioApi.checkScenarioToCompare(get(api), scenarioToCompare.scenario.id)
                                        .then(compareResponse => {
                                            if (compareResponse.assignmentsInCalculation.length == 0) {
                                                console.log('ScenarioOne, reloadData, scenarioToCompareTimeoutHandler, all assignments calculated');
                                                $planning.scenarioToCompare.calculatingInProgress = false;
                                                clearInterval(scenarioToCompareTimeoutHandler);
                                                getScenarioToCompare(scenarioToCompare.scenario.id)
                                                    .then(newScenarioToCompare => {
                                                        $scenarioManager.setScenarioToCompare(newScenarioToCompare);
                                                        $scenarioManager = $scenarioManager;
                                                    })
                                                    .catch(err => {
                                                        console.error('ScenarioOne, reloadData, scenarioToCompareTimeoutHandler, getScenarioToCompare error', err);
                                                    });
                                            } else {
                                                console.log('ScenarioOne, reloadData, scenarioToCompareTimeoutHandler, assignmentsInCalculation', compareResponse.assignmentsInCalculation);
                                            }
                                        })
                                        .catch(err => {
                                            $planning.scenarioToCompare.calculatingInProgress = false;
                                            console.error('ScenarioOne, reloadData, scenarioToCompareTimeoutHandler, error', err);
                                            clearInterval(scenarioToCompareTimeoutHandler);
                                        });
                                }, 10 * 1000);
                            }

                        }

                        $planning.assignments = $scenarioManager.getScenarioSections(false).length;

                        $scenarioManager.orderByPriority();
                        tacticalPlanLoaded = false;
                        $scenarioManager.setTacticalPlan(null);

                        anyAssignmentsReadyToShow = true;

                        // assignments = $scenarioManager.getScenarioSections(false); // 888

                        getRealisationStatus();

                        fetchEstimates();

                        if (scenario?.tactical_plan_id) {
                            Promise.all([
                                TacticalPlanApi.getOne(get(api), scenario.tactical_plan_id),
                            ])
                                .then(response => {
                                    tacticalPlan = response[0];
                                    $scenarioManager.setTacticalPlan(tacticalPlan);
                                    $scenarioManager = $scenarioManager;
                                })
                                .catch(e => {
                                    console.error('error: ', e);
                                })
                                .finally(() => {
                                    tacticalPlanLoaded = true;
                                });
                        } else {
                            $scenarioManager = $scenarioManager;
                            tacticalPlanLoaded = true;
                        }

                        if (componentMounted) {
                            if (planningOnMap) {
                                planningOnMap.removeEventListener('zoneclick', onZoneClick);
                                planningOnMap.removeEventListener('jobdrag', onJobDrag);
                                planningOnMap.destroy(leaveMapObjects);
                            }
                            planningOnMap = new PlanningOnMap({
                                scenarioId: scenario?.id,
                                date: $planning.selectedDay,
                                assignmentQuery: {
                                    assignment_id: { $in: assignmentIds },
                                    x_target_date: $planning.selectedDay,
                                },
                                queries: {},
                                selectedAssignment: null,
                                selectJobs: $features.feature_ux_improvements
                            });
                            if (zoomMap && $clientSettings.display.zoom_map_on_every_change) {
                                planningOnMap.zoomToAssignment({
                                    assignment_id: { $in: assignmentIds },
                                    x_target_date: $planning.selectedDay,
                                });
                            }
                            planningOnMap.addEventListener('zoneclick', onZoneClick);
                            planningOnMap.addEventListener('jobdrag', onJobDrag);
                            if ($features.feature_ux_improvements) {
                                planningOnMap.addEventListener('jobclick', onJobRightClick);
                                planningOnMap.addEventListener('jobrightclick', onJobRightClick);
                                planningOnMap.addEventListener('jobselect', onJobSelect);
                            }
                        }

                    })
                    .catch(err => {
                        console.error('ScenarioOne, reloadData, getSuggestedAssignmentIds', err);
                        error = err;
                    }).finally(() => {
                        loading = false;
                        zoomMap = true;
                    });
            })
            .catch(err => {
                console.error('ScenarioOne, reloadData', err);
                loading = false;
                zoomMap = true;
                error = err;
            });
    }

    function calculateRoutingProgress() {
        let progress = null;
        let assignments = $scenarioManager.getScenarioSections(false);
        for (let assignment of assignments) {
            if (assignment.isRouteInCalculation()) {
                let routeProgress = assignment.getRouteCalculationProgress();
                if (routeProgress) {
                    if (progress === null) {
                        progress = Object.assign({}, routeProgress);
                    } else {
                        progress.done += routeProgress.done || 0;
                        progress.total += routeProgress.total || 0;
                    }
                }
            }
        }
        if (progress !== null) {
            $planning.routingProgress = progress.total
                ? Math.round((100 * progress.done) / progress.total)
                : 0;
        } else {
            $planning.routingProgress = null;
        }
    }

    let getRealisationStatusTimeout: any = null;

    async function getRealisationStatus() {
        if (!getRealisationStatusShouldWork || getRealisationStatusPending) {
            console.debug('getRealisationStatus, getRealisationStatusShouldWork is false');
            return;
        }

        let thereIsAnyAssInProcess =
            $scenarioManager
                .getScenarioSections(false)
                .filter(a => a.get().stage_target_processed === false).length > 0;
        if (thereIsAnyAssInProcess) {
            console.debug('getRealisationStatus, thereIsAnyAssInProcess');
            return;
        }

        if (getRealisationStatusTimeout) {
            clearTimeout(getRealisationStatusTimeout);
            getRealisationStatusTimeout = null;
        }

        getRealisationStatusTimeout = setTimeout(
            getRealisationStatus,
            Const.RELOAD_ADHOC_CHECKING_INTERVAL_SECONDS * 1000
        );

        lockedAssignmentIds = $scenarioManager
            .getScenarioSections(false)
            .filter(a => a.hasRouteBeenEverDownloaded())
            .map(a => a.getId());

        if (lockedAssignmentIds.length > 0) {
            getRealisationStatusPending = true;
            AssignmentApi.getRealisationStatus(get(api), lockedAssignmentIds, true)
                .then(response => {
                    console.log('getRealisationStatus, getRealisationStatus', response);
                    for (let i = 0, ii = response.length; i < ii; i++) {
                        if (typeof response[i]?.id === 'number') {
                            let assignment = $scenarioManager.getAssignmentById(response[i].id);
                            if (assignment) {
                                assignment.updateRealisationStatus(response[i]);
                            }
                        }
                    }
                    $scenarioManager = $scenarioManager;
                })
                .catch(err => {
                    console.error('getRealisationStatus, getRealisationStatus', err);
                })
                .finally(() => {
                    getRealisationStatusPending = false;
                });
        } else {
            console.debug('getRealisationStatus, no locked assignments');
        }

        ScenarioApi.getAdHocAssignment(get(api), scenario.id)
            .then(response => {
                console.log('getRealisationStatus, getAdHocAssignment', response);
                if (
                    response &&
                    response.details &&
                    Object.values(response.details).reduce((a, b) => a + b, 0) > 0
                ) {
                    adHocAssignment = new Assignment(response);
                    let quickCategories = new QuickCategories(quickCategoriesConfig);
                    adHocAssignment.setQuickCategories(quickCategories);
                    adHocAssignment.updateQuickCategories();
                    $scenarioManager.updateSection(adHocAssignment);
                    $scenarioManager = $scenarioManager;
                    $planning.adHocModeEnabled = quickCategories.getTotalQuantity() > 0;
                } else if (adHocAssignment) {
                    adHocAssignment = null;
                }
            })
            .catch(err => {
                console.error('getRealisationStatus, getAdHocAssignment', err);
            });

        AssignmentJobApi.getCountWithQueriesAdHocsMany2(
            get(api),
            $scenarioManager.getScenarioSections(false).map(a => a.getId()),
            $planning.selectedDay,
            $planning.currentScenarioId
        )
            .then(response => {
                console.log('getRealisationStatus, getCountWithQueriesAdHocsMany2', response);
                if (response) {
                    $planning.adHocModeEnabled = false;
                    response.group_by_assignments.forEach(output => {
                        $scenarioManager.updateAssignmentAdhoc(output.assignment_id, output.count);

                        let quickCategories = $scenarioManager
                            .getAssignmentById(output.assignment_id)
                            ?.getQuickCategories();
                        if (!quickCategories) {
                            quickCategories = new QuickCategories(quickCategoriesConfig);
                        }
                        quickCategories.updateAdhoc(
                            'to_assign',
                            output.group_by_process_stage?.to_assign || 0
                        );
                        quickCategories.updateAdhoc(
                            'to_approve',
                            output.group_by_process_stage?.to_approve || 0
                        );
                        quickCategories.updateAdhoc(
                            'to_send',
                            output.group_by_process_stage?.to_send || 0
                        );
                        quickCategories.updateAdhoc(
                            'in_progress',
                            output.group_by_process_stage?.in_progress || 0
                        );
                        $scenarioManager
                            .getAssignmentById(output.assignment_id)
                            ?.setQuickCategories(quickCategories);

                        if (output.count > 0) {
                            $planning.adHocModeEnabled = true;
                        }
                    });

                    $scenarioManager = $scenarioManager;
                }
            })
            .catch(err => {
                console.error('getRealisationStatus, getCountWithQueriesAdHocsMany2', err);
            });

        // check if vehicle reached next route point - if yes, turn off possibility to send the route (must be recalculated)
        $scenarioManager.getScenarioSections(false).forEach(assignment => {
            if (
                assignment.isLocked() &&
                assignment.isRouteCalculated()
                // assignment.getRsa().isBaseRouteComparable()
            ) {
                assignment.deliveryListApp
                    .setAssignmentId(assignment.getId())
                    .loadExtendedRoutePoints()
                    .then(() => {
                        assignment
                            .saveNextRoutePoints(
                                assignment.deliveryListApp.getNextRoutePointJobId()
                            )
                            .then(_ => {
                                $scenarioManager.updateSection(assignment);
                            });
                    });
            }
        });
    }

    let fetchEstimateQueue: number[] = [];
    let fetchEstimatePending: number[] = [];
    function fetchEstimates() {
        console.log(
            'fetchEstimates a',
            fetchEstimateQueue.join(','),
            fetchEstimatePending.join(',')
        );
        //let assignments = $scenarioManager.getScenarioSections();
        for (let assignment of $scenarioManager.getScenarioSections(false)) {
            // 888
            if (
                !assignment.hasEstimation() &&
                assignment.canRouteBeCalculated() &&
                !assignment.isRouteCalculated() &&
                fetchEstimateQueue.indexOf(assignment.getId()) == -1
            ) {
                fetchEstimateQueue.push(assignment.getId());
            }
        }
        console.log(
            'fetchEstimates b',
            $scenarioManager.getScenarioSections(false),
            fetchEstimateQueue.join(',')
        );
        if (fetchEstimateQueue.length) {
            for (let i = fetchEstimatePending.length; i < 5; i++) {
                for (let j of fetchEstimateQueue) {
                    if (fetchEstimatePending.indexOf(j) == -1) {
                        fetchEstimatePending.push(j);
                        AssignmentApi.getEstimation(get(api), j).then(
                            (estimate: EstimationRouteType) => {
                                let assignment = $scenarioManager
                                    .getScenarioSections(false)
                                    .filter(a => a.getId() == j)[0]; // 888
                                console.log(
                                    'onEstimate',
                                    assignment ? assignment.getId() : null,
                                    estimate
                                );
                                if (assignment) {
                                    if (estimate) {
                                        assignment.setEstimation(estimate);
                                        // assignments = assignments; // 888
                                    }
                                    fetchEstimateQueue.splice(
                                        fetchEstimateQueue.indexOf(assignment.getId()),
                                        1
                                    );
                                    fetchEstimatePending.splice(
                                        fetchEstimatePending.indexOf(assignment.getId()),
                                        1
                                    );
                                    fetchEstimates();
                                }
                                $scenarioManager = $scenarioManager;
                            }
                        );
                    }
                }
            }
        }
    }

    // function reloadVehiclesOnMap() {
    //     if (vehiclesOnMap) {
    //         let vehicles = $scenarioManager.getVehiclesWithColors();
    //         let assignments = $scenarioManager.getScenarioSections(false);
    //         vehiclesOnMap.setPointsToTrack(vehicles, assignments);
    //     } else {
    //         console.error('VehiclesOnMap, vehiclesOnMap is null');
    //     }
    // }

    function onZoneClick(evt: any) {
        planningOnMap.removeEventListener('zoneclick', onZoneClick);
        planningOnMap.removeEventListener('jobdrag', onJobDrag);
        planningOnMap.destroy(false);
        Navigation.navigate(`/planning/${evt.detail.assignmentId}`);
    }

    function onJobDrag(evt: any) {
        if (evt.detail.target) {
            destroyMonitMapBar();
            let data = {
                query: {
                    job_id: { $in: evt.detail.jobs.map((j: any) => j.jobId) },
                },
                assignment_id: evt.detail.target,
            };
            AssignmentJobApi.changeAssignments(get(api), data)
                .then(res => {
                    console.log('res', res);
                    zoomMap = false;
                    reloadData();
                })
                .catch(err => {
                    console.log('err', err);
                })
                .finally(() => {
                    console.log('finally');
                });
        }
    }

    function onJobRightClick(evt: any) {
        AssignmentJobApi.getWithQueries(get(api), {job_id: evt.detail.jobId, x_target_date: $planning.selectedDay}, new ApiFilterBuilder(1)).then((response) => {
            if (response.length == 1) {
                let assignmentJob = new AssignmentJob(response[0]);
                let assignmentId = 0;
                if (assignmentJob.getAssignmentId() > 0) {
                    assignmentId = assignmentJob.getAssignmentId();
                } else if (assignmentJob.hasSuggestedAssignment()) {
                    assignmentId = assignmentJob.getSuggestedAssignmentId() || 0;
                }
                let assignment = $scenarioManager.getAssignmentById(assignmentId);
                if (assignment) {
                    let menuActionHandler = async function(done: boolean = true, fromAssignment: Assignment|null = null, toAssignment: Assignment|null = null) {
                        if (done) {
                            if (assignmentJob.getAssignmentId() > 0) {
                                planningOnMap.jobReassign(assignmentJob.getJobId(), assignmentJob.getAssignmentId(), assignmentJob.getId());
                            } else {
                                planningOnMap.jobReassign(assignmentJob.getJobId(), 0, null);
                            }
                            //let newAssignment = $scenarioManager.getAssignmentById(assignmentJob.getAssignmentId());
                            if (toAssignment) {
                                toAssignment = await toAssignment.reload();
                                $scenarioManager.updateSection(toAssignment);
                                toAssignment.updateQuickCategories();
                                if (toAssignment.isRouteInCalculation()) {
                                    $scenarioManager.getStageTargetProcessingChecker()?.addAssignment(toAssignment.getId());
                                    $scenarioManager.setAssignmentBusyStatus(toAssignment.getId(), true);
                                } else {
                                    $scenarioManager.setAssignmentBusyStatus(toAssignment.getId(), false);
                                }
                            }
                            assignment.updateQuickCategories();
                            $scenarioManager = $scenarioManager;
                            fetchEstimates();
                        } else {
                            if (toAssignment) {
                                $scenarioManager.setAssignmentBusyStatus(toAssignment.getId(), true);
                                $scenarioManager = $scenarioManager;
                            }
                        }
                    };
                    let menu = assignmentJob.setMenuOptions(assignment, {
                        assign_to_last_used: menuActionHandler,
                        assign: menuActionHandler,
                        assign_suggestion: menuActionHandler,
                        select_another_assignment: menuActionHandler,
                        reject: menuActionHandler,
                        change_target_date: menuActionHandler
                    });
                    MenuContextFactory.set(menu);
                    MenuContextFactory.setCoords(
                        evt.detail.page[0],
                        evt.detail.page[1]
                    );
                    MenuContextFactory.open();
                }
            }
        });
    }

    function onJobSelect(evt: any) {
        console.log('ScenarioOne, onJobSelect', evt.detail);
        if (evt.detail.selection.length) {
            let selectedItems = evt.detail.selectionDetailed.filter(aj => aj.job_id).map(aj => new AssignmentJob(aj));
            if (!monitMapBar) {
                createMonitMapBar(selectedItems);
            } else {
                monitMapBar.$set({selectedItems: selectedItems});
            }
        } else {
            destroyMonitMapBar();
        }
    }

    async function prepareEmptyScenarioManager() {
        $scenarioManager.clear();
        $scenarioManager.setScenario(scenario);
        $scenarioManager.setAllScenarios(allScenarios);
        let emptyAssignments = await AssignmentApi.getWithCountersByScenarioId(
            get(api),
            scenario.id,
            $planning.selectedDay
        );
        emptyAssignments.forEach(eass => {
            let assignment = new Assignment(eass);
            assignment.setQuickCategories(new QuickCategories(quickCategoriesConfig));
            $scenarioManager.updateSection(assignment);
        });
        $scenarioManager.setVehicles($vehiclesManager.getItems().map(v => v.get()));
        $scenarioManager.orderByPriority();
        $scenarioManager = $scenarioManager;
    }

    onMount(() => {
        componentMounted = true;
        getRealisationStatusShouldWork = true;
        $scenarioManager.clear();
        $planning.selectedAssignments = [];
        $planning.planningStarted = scenario.planning_started !== null;
        $currentMainFilter = FilterPlanning;
        anyAssignmentsReadyToShow = false;

        $scenarioManager.setStageTargetProcessingChecker(
            new StageTargetProcessingChecker(
                $api,
                scenario.id,
                (diffInfo: FunctionOnChangeIdsType) => {
                    (async () => {
                        // console.log(
                        //     'StageTargetProcessingChecker, onChange',
                        //     diffInfo,
                        //     Array.from(diffInfo.keys()).map(Number)
                        // );

                        // checking
                        if (diffInfo.size === 0) {
                            console.warn('StageTargetProcessingChecker, onChange, ids is empty');
                            return;
                        }

                        // FIXME: this is needed only to get details.jobs from assignment === 0 -- needed to simplify / optimize
                        let assToAssign: AssignmentResponseType[] =
                            await AssignmentApi.getWithCountersByScenarioId(
                                get(api),
                                scenario.id,
                                $planning.selectedDay
                            ).catch(err => {
                                console.error(
                                    'StageTargetProcessingChecker, onChange, getWithCountersByScenarioId',
                                    err
                                );
                            });
                        if (assToAssign) {
                            let a = $scenarioManager
                                .getScenarioSections(false)
                                .find(a => a.getId() === 0);
                            console.log('getRealisationStatus, updateAssignment1', assToAssign);
                            if (a) {
                                a.getQuickCategories().updateQuantity(
                                    'to_assign',
                                    assToAssign[0].details?.jobs || 0
                                );
                                console.log(
                                    'getRealisationStatus, updateAssignment3',
                                    $scenarioManager
                                        .getScenarioSections(false)
                                        .find(a => a.getId() === 0)
                                        ?.getQuickCategories()
                                        .getQuantity('to_assign')
                                );
                            }
                        }

                        // update assignments
                        let assToUpdate = await AssignmentApi.getAll(
                            get(api),
                            Array.from(diffInfo.keys()).map(Number)
                        );

                        assToUpdate.forEach(ass => {
                            console.log('assignment=1519, assToUpdate', ass);
                            $scenarioManager.updateAssignment(ass);
                            if (ass.stage_target == 'assign' && ass.stage_target_processed) {
                                AssignmentApi.getEstimation(get(api), ass.id).then(
                                    (estimate: EstimationRouteType) => {
                                        if (estimate) {
                                            let assignment = $scenarioManager
                                                .getScenarioSections(false)
                                                .filter(a => a.getId() == ass.id)[0];
                                            if (assignment) {
                                                assignment.setEstimation(estimate);
                                            }
                                        }
                                        $scenarioManager = $scenarioManager;
                                    }
                                );
                            }
                        });

                        // clearing
                        diffInfo.forEach((value, id) => {
                            if (value.lastStatus === true) {
                                $scenarioManager.setAssignmentBusyStatus(id, false);
                                $scenarioManager
                                    .getStageTargetProcessingChecker()
                                    ?.removeAssignment(id);
                            } else {
                                console.log(
                                    'StageTargetProcessingChecker, onChange, stage HAS NOT lastStatus',
                                    id
                                );
                            }
                        });
                        $scenarioManager = $scenarioManager;
                    })();
                }
            )
        );

        (async () => {
            if (
                scenario.tactical_plan_id !== null &&
                !onlyLocked &&
                $planning.forceToSetTacticalPlan
            ) {
                console.log('ScenarioOne, onMount, with reset tactical plan');
                waitingMessage = $_('Recalculating the tactical plan');
                if (true) {
                    // feature_new_set_pt_logic
                    anyAssignmentsReadyToShow = false;
                    // if ($planning.planningStarted === false && $planning.currentScenarioId) {
                    //     await resetAssignments(scenario.id, scenario.tactical_plan_id);
                    // }
                    await prepareEmptyScenarioManager();
                    anyAssignmentsReadyToShow = true;
                }
                ScenarioApi.changeTacticalPlan(get(api), scenario.id, scenario.tactical_plan_id)
                    .then(async response => {
                        if ($planning.planningStarted === false && $planning.currentScenarioId) {
                            await setStartPlanning($planning.currentScenarioId, true);
                        } else {
                            console.warn(
                                'ScenarioOne, onMount, setStartPlanning is not called',
                                $planning.planningStarted,
                                $planning.currentScenarioId
                            );
                        }
                        reloadData();

                        let busyIds = $scenarioManager
                            .getScenarioSections(false)
                            .filter(a => a.get().stage_target_processed === false)
                            .map(a => a.getId());
                        busyIds.forEach(id => {
                            $scenarioManager.getStageTargetProcessingChecker()?.addAssignment(id);
                            $scenarioManager.setAssignmentBusyStatus(id, true);
                        });
                        $scenarioManager.getStageTargetProcessingChecker()?.start();
                    })
                    .catch(err => {
                        error = err;
                        loading = false;
                    });
            } else {
                console.info('ScenarioOne, onMount, without reset tactical plan');
                reloadData();
            }
        })();

        return () => {
            clearInterval(scenarioToCompareTimeoutHandler);
            $scenarioManager.getStageTargetProcessingChecker()?.stop();
            // vehiclesOnMap.destroy();
            componentMounted = false;
            getRealisationStatusShouldWork = false;
            $currentMainFilter = null;
            if (planningOnMap) {
                planningOnMap.removeEventListener('zoneclick', onZoneClick);
                planningOnMap.removeEventListener('jobdrag', onJobDrag);
                planningOnMap.destroy(leaveMapObjects);
            } else {
                captureMessage('captureMessage: ScenarioOne, onMount, planningOnMap is null');
            }
            if (getRealisationStatusTimeout) clearTimeout(getRealisationStatusTimeout);
        };
    });

    // $: {
    //     vehiclesOnMap.setVisibility($features.feature_1360_track_vehicles);
    //     vehiclesOnMap.setRandomMode($features.feature_1360_track_vehicles_random);
    //     vehiclesOnMap.update();
    // }

    $: {
        if ($planning.forceReload) {
            setTimeout(() => {
                $planning.forceReload = false;
                reloadData();
            }, 0);
        }
    }

    if (scenario.id) $planning.currentScenarioId = scenario.id;

    $: {
        calculateRoutingProgress();
    }

    $: {
        visibleAssignmentIds = $scenarioManager
            .getScenarioSections(false, false, $planning.adHocMode)
            .filter(
                assignment =>
                    $planning.currentQuickCategoryId === '' ||
                    assignment.getQuickCategories().getQuantity($planning.currentQuickCategoryId) > 0 ||
                    assignment.toCompare()?.getQuickCategories().getQuantity($planning.currentQuickCategoryId) > 0
            )
            .map(assignment => assignment.getId());
        if (planningOnMap) {
            let assignmentQuery: any = {
                assignment_id: { $in: visibleAssignmentIds },
                x_target_date: $planning.selectedDay,
            };
            switch ($planning.currentQuickCategoryId) {
                case 'to_assign':
                    assignmentQuery.suggested_assignment_id = null;
                    break;
                case 'to_approve':
                    assignmentQuery.suggested_assignment_id = assignmentQuery.assignment_id;
                    assignmentQuery.assignment_id = 0;
                    break;
            }
            if (previousAssignmentQuery != JSON.stringify(assignmentQuery)) {
                previousAssignmentQuery = JSON.stringify(assignmentQuery);
                if (zoomMap) {
                    planningOnMap.addEventListener(
                        'filterset',
                        function (evt: any) {
                            if ($clientSettings.display.zoom_map_on_every_change) {
                                planningOnMap.zoomBestFit();
                            }
                        },
                        { once: true }
                    );
                }
                planningOnMap.updateConfig({ assignmentQuery: assignmentQuery, externalJobs: null });
            }
        }

        /*if (
            $scenarioManager.getScenarioSections(false).filter(a => a.isLocked()).length !=
            lockedAssignmentIds.length
        ) {
            // 888
            getRealisationStatus();
        }*/

        selectableAssignmentIds = $scenarioManager
            .getScenarioSections(false)
            .filter(
                assignment =>
                    assignment.isDefault() && visibleAssignmentIds.includes(assignment.getId())
            )
            .map(assignment => assignment.getId());
    }

    afterUpdate(() => {
        if (!loading) {
            planningContext = new PlanningContext(
                vehicles,
                $scenarioManager.getScenarioSections(false),
                scenario
            );
            planningReport = new ValidatorReport();
            planningValidator.checkIfAllVehiclesAreDispatchedInAssignments(
                planningReport,
                planningContext
            );
            planningReport = planningReport;
        }
    });

    let assignments: Assignment[] = [];
    $: {
        assignments = $scenarioManager.getScenarioSections(false, $debug, $planning.adHocMode);
    }
</script>

{#if loading && !anyAssignmentsReadyToShow}
    <div in:fade={{ delay: 1000, duration: 500 }}>
        <TemplateInner>
            <MessageInline text={waitingMessage} />
            <Spinner />
        </TemplateInner>
    </div>
{:else if error && !loading}
    {#if error === 'assignment_too_much_jobs'}
        <TemplateInner>
            <Separator />
            <ErrorInline text={$_(error)} />
            <Separator />
            <ButtonMain
                on:click={() => {
                    Navigation.navigate(`/tactical-plan/${scenario.tactical_plan_id}`);
                }}
                text={$_('Go to tactical plan')}
            />
        </TemplateInner>
    {:else}
        <MessageDanger {error} />
    {/if}
{/if}
{#if anyAssignmentsReadyToShow}
    <div
        style="flex:0; margin: 5px 0 5px 0; min-height: 30px; display: flex; align-items: center; justify-content: flex-start;"
    >
        {#if $planning.scenarioToCompare.calculatingInProgress && !loading}
            <div style="width: 24px;">
                <IconPending spin={true} scale={0.6} />
            </div>
        {/if}
        {#if loading}
            <div
                style="display:flex;flex-direction: row; align-items: center; justify-content: flex-start"
            >
                <div style="width: 24px;">
                    <IconPending spin={true} scale={0.6} />
                </div>
                <div style="font-size: 14px; font-weight: 600; width: 300px; ">
                    {get(_)('Trwa przeliczanie planu taktycznego')}...
                </div>
            </div>
        {:else}
            <ScenarioOneSettings
                disabled={$readonlyMode}
                scenarioManager={$scenarioManager}
                {tacticalPlanLoaded}
                onChange={async (tacticalPlanId, resetWithManualChange, ignoreDefinedOrder) => {
                    if ($planning.scenarioToCompare?.enabled && tacticalPlanId === $planning.scenarioToCompare?.tacticalPlanId) {
                        $planning.scenarioToCompare = {
                            enabled: false,
                            scenarioId: null,
                            tacticalPlanId: null,
                            calculatingInProgress: false
                        };
                    }
                    if (planningOnMap) {
                        planningOnMap.removeEventListener('zoneclick', onZoneClick);
                        planningOnMap.removeEventListener('jobdrag', onJobDrag);
                        planningOnMap.destroy(leaveMapObjects);
                    }
                    loading = true;
                    error = '';

                    if (true) {
                        // $features.feature_new_set_pt_logic
                        anyAssignmentsReadyToShow = false;
                        await resetAssignments(scenario.id, tacticalPlanId);
                        await prepareEmptyScenarioManager();
                        anyAssignmentsReadyToShow = true;
                    }

                    ScenarioApi.changeTacticalPlan(get(api), scenario.id, tacticalPlanId)
                        .then(() => {
                            waitingMessage = $_('Changing tactical plan');
                            reloadData();
                        })
                        .catch(err => {
                            error = err;
                            loading = false;
                        });
                }}
                onChangeTacticalPlanToCompare={async(tacticalPlanId, withReset, ignoreDefinedOrder) => {
                        loading = true;
                        if (planningOnMap) {
                            planningOnMap.removeEventListener('zoneclick', onZoneClick);
                            planningOnMap.removeEventListener('jobdrag', onJobDrag);
                            planningOnMap.destroy(false);
                        }
                        if (tacticalPlanId===null) {
                            console.log('#planning-compare-scenario, onChangeTacticalPlanToCompare, disabling comparison');
                            $planning.scenarioToCompare = {
                                enabled: false,
                                scenarioId: null,
                                tacticalPlanId: null,
                                calculatingInProgress: false
                            };
                            $planning.forceReload = true;
                            return;
                        }
                        console.log('#planning-compare-scenario, onChangeTacticalPlanToCompare, $scenarioManager.getAllScenariosToCompare()', $scenarioManager.getAllScenariosToCompare());
                        let scenarioToCompare = $scenarioManager.getAllScenariosToCompare().find(s => s.tactical_plan_id === tacticalPlanId);
                        if (scenarioToCompare) {
                            console.log('#planning-compare-scenario, onChangeTacticalPlanToCompare, scenarioToCompare exists', scenarioToCompare.id, scenarioToCompare.tactical_plan_id);
                            if (withReset) {
                                console.log('#planning-compare-scenario, onChangeTacticalPlanToCompare, resetting scenarioToCompare assignments due to manual change', scenarioToCompare.id, tacticalPlanId);
                                await resetAssignments(scenarioToCompare.id, tacticalPlanId);
                                //await ScenarioApi.changeTacticalPlan(get(api), scenarioToCompare.id, tacticalPlanId);
                                await ScenarioApi.initScenarioToCompare(get(api), scenarioToCompare.id, tacticalPlanId);
                            } else {
                                console.log('#planning-compare-scenario, onChangeTacticalPlanToCompare, no reset of scenarioToCompare assignments', scenarioToCompare.id, tacticalPlanId);
                            }
                            $planning.scenarioToCompare = {
                                enabled: true,
                                scenarioId: scenarioToCompare.id,
                                tacticalPlanId: tacticalPlanId,
                                calculatingInProgress: true
                            };
                            $planning.forceReload = true;
                        } else {
                            console.log('#planning-compare-scenario, onChangeTacticalPlanToCompare, creating new scenario due to does not exists with tacticalPlanId', tacticalPlanId);
                            await ScenarioApi.setAsSelected(get(api), $scenarioManager.getScenarioId());
                            let newScenario = await ScenarioApi.create(get(api), {
                                day: $planning.selectedDay,
                                name: 'Alternative scenario',
                                planning_started: null,
                                collection_id: scenario.collection_id,
                                tactical_plan_id: tacticalPlanId,
                                to_compare_only: true,
                                selected: false,
                                ignore_defined_order: ignoreDefinedOrder
                            });
                            console.log('#planning-compare-scenario, onChangeTacticalPlanToCompare, newScenario created', newScenario.id, newScenario.tactical_plan_id);
                            await resetAssignments(newScenario.id, tacticalPlanId);
                            //await ScenarioApi.changeTacticalPlan(get(api), newScenario.id, tacticalPlanId);
                            await ScenarioApi.initScenarioToCompare(get(api), newScenario.id, tacticalPlanId);
                            $tacticalPlanStore.forceReload = true;
                            $planning.scenarioToCompare = {
                                enabled: true,
                                scenarioId: newScenario.id,
                                tacticalPlanId: newScenario.tactical_plan_id,
                                calculatingInProgress: true
                            };
                            $planning.forceReload = true;
                        }
                        console.log('#planning-compare-scenario, onChangeTacticalPlanToCompare, finished', $planning.scenarioToCompare)
                }}
            />
        {/if}
    </div>
    {#if planningReport.getMessagesForRuleId(null).length > 0}
        <div>
            {#each planningReport.getMessagesForRuleId(null) as warning}
                <WarningInline text={$_(warning.message)} />
            {/each}
        </div>
    {/if}
    <div style="margin: 22px 0 17px 0;">
        <ScenarioOneQuickCategories />
    </div>
    <div
        style="margin: 20px 0 0 0; overflow-x: hidden; flex:1; display: flex; flex-direction: column; overflow-y: auto; min-height: 300px;"
    >
        <div class="section-list" class:simpleView={false}>
            {#if adHocAssignment && false}
                <!-- FIXME: remove after ad-hoc assignment is implemented -->
                <!--                <div transition:scale={{ duration: 200, start: 0 }}>-->
                <!--                    <AssignmentSummary-->
                <!--                        filterHideMethod={$planning.quickCategoryHideMethod}-->
                <!--                        itemVisible={$planning.currentQuickCategoryId === '' ||-->
                <!--                            adHocAssignment-->
                <!--                                .getQuickCategories()-->
                <!--                                .getQuantity($planning.currentQuickCategoryId) > 0}-->
                <!--                        assignment={adHocAssignment}-->
                <!--                        {vehicles}-->
                <!--                        {selectableAssignmentIds}-->
                <!--                        showHeader={true}-->
                <!--                        onClickHeader={() => {-->
                <!--                            leaveMapObjects = true;-->
                <!--                            Navigation.navigate(`/planning/${adHocAssignment.getId()}`);-->
                <!--                        }}-->
                <!--                    />-->
                <!--                </div>-->
            {/if}
            {#each assignments as assignment, index}
                <!--                    <div transition:scale={{ duration: 200, start: 0 }}>-->
                <AssignmentSummary
                    filterHideMethod={$planning.quickCategoryHideMethod}
                    itemVisible={visibleAssignmentIds.includes(assignment.getId())}
                    assignment={assignment}
                    {vehicles}
                    toAssign={assignment.getQuickCategories().getQuantity('to_assign')}
                    {visibleAssignmentIds}
                    disabled={loading || $readonlyMode || assignment.isFake()}
                    showSelectCheckbox={true}
                    selectableAssignmentIds={loading ? [] : selectableAssignmentIds}
                    showHeader={index === 0}
                    onClickHeader={() => {
                        if (!assignment.isFake()) {
                            leaveMapObjects = true;
                            Navigation.navigate(`/planning/${assignment.getId()}`);
                        }
                    }}
                    onColorChange={evt => {
                        if (evt.target) {
                            setAssignmentColor(assignment.getId(), evt.target.value);
                        }
                    }}
                />
                {#if assignment.toCompare()}
                    <AssignmentSummary
                        filterHideMethod={$planning.quickCategoryHideMethod}
                        itemVisible={visibleAssignmentIds.includes(assignment.getId())}
                        assignment={assignment.toCompare()}
                        isToCompareOnly={true}
                        {vehicles}
                        toAssign={assignment.toCompare()?.getQuickCategories().getQuantity('to_assign') || 0}
                        {visibleAssignmentIds}
                        disabled={true}
                        showSelectCheckbox={true}
                        selectableAssignmentIds={loading ? [] : selectableAssignmentIds}
                        showHeader={false}
                        onClickHeader={() => {
                            if (assignment.toCompare().getId()) {
                                leaveMapObjects = true;
                                Navigation.navigate(`/planning/${assignment.toCompare().getId()}`);
                            }
                        }}
                        onColorChange={evt => {
                            if (evt.target) {
                                setAssignmentColor(assignment.getId(), evt.target.value);
                            }
                        }}
                    />
                {/if}
            {:else}
                <TemplateInner>
                    <MessageNotFound text={$_('Not found any jobs for this day')} />
                </TemplateInner>
            {/each}

            <!--{#if $planning.assignments === 0}-->
            <!--    <div in:fade={{ delay: 2000, duration: 500 }}>-->
            <!--        <TemplateInner>-->
            <!--            <MessageNotFound text={$_('Not found any jobs for this day')} />-->
            <!--        </TemplateInner>-->
            <!--    </div>-->
            <!--{/if}-->
        </div>
    </div>
{/if}

<style>
    .section-list {
        margin-bottom: 50px;
    }
</style>
